<script lang="ts" setup>
import {
  TuiDropdown,
  TuiDropdownHeader,
  TuiDropdownItem,
  TuiDropdownItemGroup,
  TuiSidebar,
  TuiVerticalNav,
  TuiVerticalNavLink,
} from '@clickbar/tailwindui-vue'
import { Link } from '@inertiajs/vue3'
import { Bug, ChevronsUpDown, Search } from '@lucide/vue'

import AppLogo from '@/Components/AppLogo.vue'
import { route, type StrictRouteName } from '@/Composables/route'
import { useCommandPalette } from '@/Composables/useCommandPalette'
import { useEnvironment, useUser } from '@/Composables/spaceship'
import { navigationIcon } from '@/lib/navigationIcons'
import { openBugReport } from '@/utils/bugReport'
import { logout } from '@/utils/logout'
import { resetLocalCache } from '@/utils/resetLocalCache'

import type { NavigationItem } from '@/Types'

defineOptions({
  inheritAttrs: false,
})

defineProps<{
  items: NavigationItem[]
}>()

const user = useUser()
const environment = useEnvironment()
const { open: openCommandPalette } = useCommandPalette()

const userNavigationItems: {
  name: string
  routeName?: StrictRouteName
  onClick?: () => void
  method?: string
  as?: string
}[] = [
  {
    name: 'Profil',
    routeName: 'profile.show',
  },
  {
    name: 'Abmelden',
    onClick: logout,
    as: 'button',
  },
]
</script>

<template>
  <TuiSidebar class="relative h-full" color="primary" v-bind="$attrs">
    <div class="-mt-5 bg-white py-6 pr-4">
      <Link class="text-primary-600" :href="route('dashboard')">
        <AppLogo class="ml-6 h-auto w-24" :environment="environment" />
      </Link>
    </div>

    <TuiVerticalNav class="mt-8 px-6!">
      <div v-for="item in items" :key="item.title">
        <TuiVerticalNavLink
          :label="item.title"
          type="inertia-link"
          :active="item.active && !item.children?.some((child) => child.active)"
          class="flex items-center"
          :href="item.children?.some((child) => child.active) ? '#' : item.url"
          :badge="item.attributes?.badge"
        >
          <template #icon="iconProps">
            <Component :is="navigationIcon(item.attributes.icon)" v-bind="iconProps" />
          </template>
        </TuiVerticalNavLink>

        <div
          v-if="item.children && (item.active || item.children.some((child) => child.active))"
          class="mt-1 flex justify-between"
        >
          <TuiVerticalNav class="flex-1 px-0!">
            <TuiVerticalNavLink
              v-for="child in item.children"
              :key="child.title"
              :label="child.title"
              type="inertia-link"
              :active="child.active"
              class="flex items-center pl-11"
              :href="child.url"
              :badge="child.attributes.badge"
            />
          </TuiVerticalNav>
        </div>
      </div>
    </TuiVerticalNav>

    <slot />

    <div class="flex-1" />

    <div class="grid gap-1 px-5">
      <button
        type="button"
        class="text-primary-200 hover:bg-primary-700/75 hover:text-primary-100 active:bg-primary-800 flex w-full items-center gap-3 rounded-md px-3.5 py-2 text-left text-sm font-medium active:text-white"
        aria-keyshortcuts="Meta+K Control+K"
        @click="openCommandPalette"
      >
        <Search class="size-5 shrink-0" aria-hidden="true" />
        <span>Suchen</span>
        <kbd
          class="border-primary-500/60 text-primary-300 ml-auto rounded border px-1.5 py-0.5 text-xs leading-none"
          >⌘ K</kbd
        >
      </button>

      <button
        type="button"
        class="text-primary-200 hover:bg-primary-700/75 hover:text-primary-100 active:bg-primary-800 flex w-full items-center gap-3 rounded-md px-3.5 py-2 text-left text-sm font-medium active:text-white"
        @click="openBugReport"
      >
        <Bug class="size-5 shrink-0" aria-hidden="true" />
        Bug melden
      </button>
    </div>

    <div v-if="user" class="mt-4 px-4">
      <!-- Profile dropdown -->
      <TuiDropdown placement="top-end" class="w-full">
        <template #activator>
          <button
            type="button"
            class="text-primary-100 group hover:bg-primary-700/75 active:bg-primary-800 mx-1 w-full rounded-md px-3.5 py-2 text-left text-sm font-medium focus:ring-2 focus:ring-white focus:outline-none active:text-white"
          >
            <span class="flex w-full items-center justify-between">
              <span class="flex min-w-0 items-center justify-between space-x-3">
                <span class="flex min-w-0 flex-1 flex-col">
                  <span class="truncate text-sm font-medium text-white">{{ user.name }}</span>
                </span>
              </span>

              <ChevronsUpDown
                class="text-primary-200 group-hover:text-primary-300 ml-1 size-5 shrink-0"
                aria-hidden="true"
              />
            </span>
          </button>
        </template>

        <TuiDropdownHeader :title="user.name" :value="user.email" />

        <TuiDropdownItemGroup>
          <TuiDropdownItem
            v-for="item in userNavigationItems"
            :key="item.name"
            :type="item.routeName ? 'inertia-link' : 'button'"
            :method="item.method"
            :as="item.as"
            :href="item.routeName ? route(item.routeName) : undefined"
            @click="item.onClick?.()"
          >
            {{ item.name }}
          </TuiDropdownItem>
        </TuiDropdownItemGroup>

        <TuiDropdownHeader title="Erweitert" value="" />

        <TuiDropdownItemGroup>
          <TuiDropdownItem type="button" as="button" @click="resetLocalCache">
            Lokalen Cache zurücksetzen
          </TuiDropdownItem>
        </TuiDropdownItemGroup>
      </TuiDropdown>
    </div>
  </TuiSidebar>
</template>
